import React, { useState, useEffect, useCallback } from "react";
import api from "../../services/api";

function formatDatetime(d) {
  if (!d) return "—";
  return new Date(d).toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function timeAgo(d) {
  if (!d) return "—";
  const diff = Date.now() - new Date(d).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function duration(start, end) {
  if (!start || !end) return null;
  const ms = new Date(end) - new Date(start);
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
}

const STATUS_COLORS = {
  completed: {
    color: "#4ade80",
    bg: "rgba(34,197,94,0.1)",
    border: "rgba(34,197,94,0.3)",
  },
  running: {
    color: "var(--teal-bright)",
    bg: "rgba(6,182,212,0.1)",
    border: "rgba(6,182,212,0.3)",
  },
  failed: {
    color: "#f87171",
    bg: "rgba(239,68,68,0.1)",
    border: "rgba(239,68,68,0.3)",
  },
  queued: {
    color: "#f59e0b",
    bg: "rgba(245,158,11,0.1)",
    border: "rgba(245,158,11,0.3)",
  },
};

export default function ExecutionsTab({ currentUser }) {
  const [executions, setExecutions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedExecution, setSelectedExecution] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showDeleted, setShowDeleted] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const LIMIT = 30;

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, limit: LIMIT };
      if (statusFilter !== "all") params.status = statusFilter;
      if (showDeleted) params.includeDeleted = true;
      const res = await api.adminGetExecutions(params);
      setExecutions(res.data.executions);
      setTotal(res.data.total);
    } catch (e) {
      setError(e.response?.data?.error || "Failed to load executions");
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, showDeleted]);

  useEffect(() => {
    load();
  }, [load]);

  const openDetail = async (execution) => {
    try {
      setDetailLoading(true);
      const res = await api.adminGetExecution(execution.id);
      setSelectedExecution(res.data.execution);
    } catch (e) {
      setError(e.response?.data?.error || "Failed to load execution details");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDelete = async (exec) => {
    if (
      !window.confirm(
        `Delete this ${exec.agentName} execution? It stays on record and can be viewed again with "Show deleted".`,
      )
    )
      return;
    try {
      setDeletingId(exec.id);
      await api.adminDeleteExecution(exec.id);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || "Failed to delete execution");
    } finally {
      setDeletingId(null);
    }
  };

  const STATUSES = ["all", "completed", "running", "failed", "queued"];

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
          flexWrap: "wrap",
          gap: "10px",
        }}
      >
        <h2
          style={{
            fontSize: "16px",
            fontWeight: "700",
            color: "var(--text-primary)",
            margin: 0,
          }}
        >
          Executions{" "}
          <span
            style={{
              color: "var(--text-muted)",
              fontWeight: "500",
              fontSize: "13px",
            }}
          >
            ({total} total)
          </span>
        </h2>
        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
          {STATUSES.map((s) => {
            const sc = STATUS_COLORS[s] || {};
            const isActive = statusFilter === s;
            return (
              <button
                key={s}
                onClick={() => {
                  setStatusFilter(s);
                  setPage(1);
                }}
                style={{
                  fontSize: "12px",
                  padding: "5px 12px",
                  borderRadius: "20px",
                  border: isActive
                    ? `1px solid ${sc.border || "rgba(6,182,212,0.5)"}`
                    : "1px solid var(--border)",
                  background: isActive
                    ? sc.bg || "rgba(6,182,212,0.1)"
                    : "transparent",
                  color: isActive
                    ? sc.color || "var(--teal-bright)"
                    : "var(--text-muted)",
                  cursor: "pointer",
                  fontWeight: isActive ? "700" : "500",
                  textTransform: "capitalize",
                }}
              >
                {s}
              </button>
            );
          })}
          <button
            onClick={() => {
              setShowDeleted((v) => !v);
              setPage(1);
            }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              fontSize: "12px",
              padding: "5px 12px",
              borderRadius: "8px",
              border: `1px solid ${showDeleted ? "rgba(239,68,68,0.4)" : "var(--border)"}`,
              background: showDeleted ? "rgba(239,68,68,0.08)" : "transparent",
              color: showDeleted ? "#f87171" : "var(--text-muted)",
              cursor: "pointer",
            }}
          >
            <span style={{ fontSize: "11px" }}>🗑</span>
            {showDeleted ? "Hide deleted" : "Show deleted"}
          </button>
          <button
            onClick={load}
            style={{
              fontSize: "12px",
              padding: "5px 12px",
              borderRadius: "8px",
              border: "1px solid var(--border)",
              background: "transparent",
              color: "var(--text-muted)",
              cursor: "pointer",
            }}
          >
            ↺
          </button>
        </div>
      </div>

      {error && (
        <div
          style={{
            padding: "10px 14px",
            background: "rgba(239,68,68,0.1)",
            border: "1px solid rgba(239,68,68,0.25)",
            borderRadius: "8px",
            color: "#f87171",
            fontSize: "13px",
            marginBottom: "16px",
          }}
        >
          {error}
          <button
            onClick={() => setError("")}
            style={{
              float: "right",
              background: "none",
              border: "none",
              color: "#f87171",
              cursor: "pointer",
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Detail Modal */}
      {selectedExecution && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.75)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            zIndex: 1000,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            padding: "20px",
            overflowY: "auto",
          }}
        >
          <div
            style={{
              background: "var(--surface)",
              border: "1px solid var(--border)",
              borderRadius: "16px",
              padding: "28px",
              width: "100%",
              maxWidth: "640px",
              marginTop: "20px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <h3 style={{ margin: 0, fontSize: "15px", fontWeight: "700" }}>
                Execution Details
              </h3>
              <button
                onClick={() => setSelectedExecution(null)}
                style={{
                  background: "none",
                  border: "none",
                  color: "var(--text-muted)",
                  cursor: "pointer",
                  fontSize: "20px",
                }}
              >
                ×
              </button>
            </div>
            <DetailRow
              label="ID"
              value={
                <code
                  style={{
                    fontSize: "11px",
                    background: "rgba(255,255,255,0.05)",
                    padding: "2px 6px",
                    borderRadius: "4px",
                  }}
                >
                  {selectedExecution.id}
                </code>
              }
            />
            <DetailRow label="Agent" value={selectedExecution.agentName} />
            <DetailRow
              label="User"
              value={`${selectedExecution.user?.fullName ? `${selectedExecution.user.fullName} (${selectedExecution.user?.username})` : selectedExecution.user?.username || selectedExecution.username}`}
            />
            <DetailRow
              label="Status"
              value={
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: "700",
                    padding: "2px 8px",
                    borderRadius: "5px",
                    background:
                      STATUS_COLORS[selectedExecution.status]?.bg ||
                      "rgba(255,255,255,0.05)",
                    border: `1px solid ${STATUS_COLORS[selectedExecution.status]?.border || "var(--border)"}`,
                    color:
                      STATUS_COLORS[selectedExecution.status]?.color ||
                      "var(--text-muted)",
                    textTransform: "uppercase",
                  }}
                >
                  {selectedExecution.status}
                </span>
              }
            />
            <DetailRow
              label="Started"
              value={formatDatetime(
                selectedExecution.startedAt || selectedExecution.createdAt,
              )}
            />
            <DetailRow
              label="Completed"
              value={formatDatetime(selectedExecution.completedAt)}
            />
            {selectedExecution.deletedAt && (
              <DetailRow
                label="Deleted"
                value={
                  <span style={{ color: "#f87171" }}>
                    {formatDatetime(selectedExecution.deletedAt)}
                  </span>
                }
              />
            )}
            <DetailRow
              label="Duration"
              value={
                duration(
                  selectedExecution.startedAt,
                  selectedExecution.completedAt,
                ) || "—"
              }
            />
            {selectedExecution.subcommand && (
              <DetailRow
                label="Subcommand"
                value={selectedExecution.subcommand}
              />
            )}
            {selectedExecution.inputs &&
              Object.keys(selectedExecution.inputs).length > 0 && (
                <div style={{ marginBottom: "10px" }}>
                  <span
                    style={{
                      fontSize: "12px",
                      color: "var(--text-muted)",
                      fontWeight: "500",
                      display: "block",
                      marginBottom: "4px",
                    }}
                  >
                    Inputs
                  </span>
                  <div
                    style={{
                      background: "rgba(255,255,255,0.03)",
                      border: "1px solid var(--border)",
                      borderRadius: "8px",
                      padding: "10px 12px",
                    }}
                  >
                    {Object.entries(selectedExecution.inputs).map(([k, v]) => (
                      <div
                        key={k}
                        style={{
                          fontSize: "12px",
                          color: "var(--text-secondary)",
                          marginBottom: "4px",
                        }}
                      >
                        <span style={{ color: "var(--text-muted)" }}>{k}:</span>{" "}
                        {String(v)}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            {selectedExecution.error && (
              <div
                style={{
                  marginTop: "10px",
                  padding: "10px 12px",
                  background: "rgba(239,68,68,0.08)",
                  border: "1px solid rgba(239,68,68,0.25)",
                  borderRadius: "8px",
                  fontSize: "12px",
                  color: "#f87171",
                }}
              >
                <strong>Error:</strong> {selectedExecution.error}
              </div>
            )}
            {selectedExecution.results && (
              <div style={{ marginTop: "12px" }}>
                <span
                  style={{
                    fontSize: "12px",
                    color: "var(--text-muted)",
                    fontWeight: "500",
                    display: "block",
                    marginBottom: "6px",
                  }}
                >
                  Results
                </span>
                <pre
                  style={{
                    background: "rgba(255,255,255,0.03)",
                    border: "1px solid var(--border)",
                    borderRadius: "8px",
                    padding: "12px",
                    fontSize: "11px",
                    color: "var(--text-secondary)",
                    maxHeight: "200px",
                    overflowY: "auto",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {typeof selectedExecution.results === "string"
                    ? selectedExecution.results
                    : JSON.stringify(selectedExecution.results, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </div>
      )}

      {loading ? (
        <div
          style={{
            textAlign: "center",
            padding: "40px",
            color: "var(--text-muted)",
          }}
        >
          Loading...
        </div>
      ) : executions.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "40px",
            color: "var(--text-muted)",
            fontSize: "13px",
          }}
        >
          No executions found
        </div>
      ) : (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            {executions.map((exec) => {
              const sc = STATUS_COLORS[exec.status] || {};
              const isDeleted = !!exec.deletedAt;
              return (
                <div
                  key={exec._id}
                  style={{
                    background: isDeleted
                      ? "rgba(239,68,68,0.03)"
                      : "var(--surface)",
                    border: `1px solid ${isDeleted ? "rgba(239,68,68,0.25)" : "var(--border)"}`,
                    borderRadius: "10px",
                    padding: "12px 16px",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    flexWrap: "wrap",
                    opacity: isDeleted ? 0.6 : 1,
                  }}
                >
                  <div style={{ flex: 1, minWidth: "150px" }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        flexWrap: "wrap",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "13px",
                          fontWeight: "600",
                          color: "var(--text-primary)",
                        }}
                      >
                        {exec.agentName}
                      </span>
                      <span
                        style={{
                          fontSize: "10px",
                          fontWeight: "700",
                          padding: "2px 7px",
                          borderRadius: "5px",
                          background: sc.bg || "rgba(255,255,255,0.05)",
                          border: `1px solid ${sc.border || "var(--border)"}`,
                          color: sc.color || "var(--text-muted)",
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                        }}
                      >
                        {exec.status}
                      </span>
                      {isDeleted && (
                        <span
                          style={{
                            fontSize: "10px",
                            fontWeight: "700",
                            padding: "2px 7px",
                            borderRadius: "5px",
                            background: "rgba(239,68,68,0.1)",
                            border: "1px solid rgba(239,68,68,0.3)",
                            color: "#f87171",
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                          }}
                        >
                          Deleted
                        </span>
                      )}
                    </div>
                    <div
                      style={{
                        fontSize: "11px",
                        color: "var(--text-muted)",
                        marginTop: "3px",
                        display: "flex",
                        gap: "10px",
                        flexWrap: "wrap",
                      }}
                    >
                      <span>
                        by{" "}
                        {exec.user?.fullName
                          ? `${exec.user.fullName} (${exec.user?.username})`
                          : exec.user?.username || exec.username}
                      </span>
                      <span>{timeAgo(exec.createdAt)}</span>
                      {exec.completedAt && exec.startedAt && (
                        <span>
                          took {duration(exec.startedAt, exec.completedAt)}
                        </span>
                      )}
                      {isDeleted && (
                        <span style={{ color: "#f87171" }}>
                          deleted {timeAgo(exec.deletedAt)}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    className="btn btn-small"
                    onClick={() => openDetail(exec)}
                    disabled={detailLoading}
                    style={{
                      fontSize: "12px",
                      padding: "5px 12px",
                      background: "rgba(255,255,255,0.06)",
                      border: "1px solid var(--border)",
                      color: "var(--text-secondary)",
                      flexShrink: 0,
                    }}
                  >
                    View
                  </button>
                  {!isDeleted && (
                    <button
                      className="btn btn-small"
                      onClick={() => handleDelete(exec)}
                      disabled={deletingId === exec.id}
                      title="Delete execution (kept on record)"
                      style={{
                        fontSize: "12px",
                        padding: "5px 12px",
                        background: "rgba(239,68,68,0.08)",
                        border: "1px solid rgba(239,68,68,0.25)",
                        color: "#f87171",
                        flexShrink: 0,
                      }}
                    >
                      {deletingId === exec.id ? "…" : "Delete"}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          {total > LIMIT && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "10px",
                marginTop: "16px",
              }}
            >
              <button
                className="btn btn-small"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                style={{
                  fontSize: "12px",
                  padding: "5px 14px",
                  opacity: page === 1 ? 0.4 : 1,
                }}
              >
                ← Prev
              </button>
              <span
                style={{
                  fontSize: "12px",
                  color: "var(--text-muted)",
                  padding: "5px 10px",
                }}
              >
                Page {page} of {Math.ceil(total / LIMIT)}
              </span>
              <button
                className="btn btn-small"
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= Math.ceil(total / LIMIT)}
                style={{
                  fontSize: "12px",
                  padding: "5px 14px",
                  opacity: page >= Math.ceil(total / LIMIT) ? 0.4 : 1,
                }}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: "12px",
        marginBottom: "10px",
      }}
    >
      <span
        style={{
          fontSize: "12px",
          color: "var(--text-muted)",
          fontWeight: "500",
          minWidth: "80px",
          paddingTop: "1px",
        }}
      >
        {label}
      </span>
      <span
        style={{ fontSize: "13px", color: "var(--text-secondary)", flex: 1 }}
      >
        {value}
      </span>
    </div>
  );
}
